import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, AlertCircle, Mail } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Verify Email screen — reached from the emailed link
// (/verify-email?token=...). Posts to /api/auth/verify-email/confirm.
// Public route: works whether the user is signed in or not (the token is
// self-contained). After success we refresh the auth context so the
// verify banner disappears everywhere.

export default function VerifyEmailScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => (params.get("token") || "").trim(), [params]);
  const { user, refresh } = useAuth() || {};

  // status: 'loading' | 'ok' | 'error'
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("This verification link is missing its token.");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email/confirm", { token });
        if (cancelled) return;
        setStatus("ok");
        setMessage(data?.message || "Email verified.");
        try { await refresh?.(); } catch { /* ignore */ }
      } catch (err) {
        if (cancelled) return;
        setStatus("error");
        setMessage(err?.response?.data?.detail || "This verification link is invalid or expired.");
      }
    })();
    return () => { cancelled = true; };
  }, [token, refresh]);

  const goHome = () => navigate(user ? "/home" : "/", { replace: true });

  return (
    <div className="screen fade-in" data-testid="verify-email-screen">
      <div className="top-bar">
        <div style={{ width: 36 }} />
        <div className="logo-mark">VERIFY</div>
        <div style={{ width: 36 }} />
      </div>

      <div style={{ textAlign: "center", paddingTop: 60 }}>
        {status === "loading" && (
          <div data-testid="verify-loading">
            <span className="spinner spinner-lg" />
            <p className="body" style={{ color: "var(--text-dim)", marginTop: 22 }}>
              Verifying your email…
            </p>
          </div>
        )}

        {status === "ok" && (
          <div data-testid="verify-success">
            <div
              style={{
                width: 64, height: 64, borderRadius: "50%",
                background: "rgba(232,211,185,0.15)", color: "var(--gold)",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 24px",
              }}
            >
              <Check size={28} strokeWidth={1.8} />
            </div>
            <h2 className="h1" style={{ fontSize: 28, marginBottom: 14, lineHeight: 1.15 }}>
              Email verified.
            </h2>
            <p className="body" style={{ color: "var(--text-dim)", maxWidth: 320, margin: "0 auto" }}>
              Your IX NOVA account is now fully secured across the website, iPhone and Android apps.
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={goHome}
              data-testid="verify-continue-btn"
              style={{ marginTop: 28, width: "100%", justifyContent: "center" }}
            >
              Continue
            </button>
          </div>
        )}

        {status === "error" && (
          <div data-testid="verify-error">
            <div
              style={{
                width: 64, height: 64, borderRadius: "50%",
                background: "rgba(255,120,120,0.14)", color: "#ffb0b0",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 24px",
              }}
            >
              <AlertCircle size={28} strokeWidth={1.8} />
            </div>
            <h2 className="h1" style={{ fontSize: 26, marginBottom: 14, lineHeight: 1.15 }}>
              Link didn't work.
            </h2>
            <p className="body" style={{ color: "var(--text-dim)", maxWidth: 340, margin: "0 auto" }}>
              {message}
            </p>
            <p className="body" style={{ color: "var(--text-dim)", marginTop: 10, fontSize: 13 }}>
              Sign in and tap "Resend verification email" from the banner on your home screen.
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={goHome}
              data-testid="verify-goto-signin-btn"
              style={{ marginTop: 24, width: "100%", justifyContent: "center", display: "flex", alignItems: "center", gap: 10 }}
            >
              <Mail size={15} strokeWidth={1.8} />
              <span>{user ? "Back to home" : "Back to sign in"}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
